/**
 * The one list of things a condition can be built on, grouped by where they
 * come from. Choosing a field IS choosing the source -- that is what makes
 * the old "pick an action, then pick a source" step unnecessary.
 *
 * Moved here out of editor-v2/utils/conditionFields.ts so both halves of this
 * codebase can offer the SAME list: editor-v2 (Vue 3 / vitest) imports it
 * relatively, the Vue-2 project settings (Jest, which cannot resolve `.ts` at
 * all) import it normally -- the same reason conditionSentence.js and
 * contextRuleHelpers.js live here.
 *
 * Plain JavaScript, no Vue, no dependency. The three lists arrive as
 * arguments rather than being read off `window`: editor-v2 has them as window
 * globals (set in composables/app/useEditorInitializationHandlers.ts), the
 * project settings load them over the network, and a module that knew about
 * either one would only work on one side.
 *
 * Type declarations for TypeScript callers live in conditionFields.d.ts --
 * runtime changes here MUST be mirrored there.
 */

import { derivedFormFields } from './formDerivedFields';

// Answer-carrying block types. Deliberately duplicated from
// src/editor-v2/forms/types.ts rather than imported: that file is the form
// BUILDER's model, it pulls in the whole block union, and it is TypeScript,
// which this project's Jest preset cannot resolve at all (see the same note
// in answerFields.js). Exported so the drift test can compare this set
// against the builder's set directly, in both directions -- catching a type
// added there (missing here) as well as one removed there (stale leftover
// here). That test lives in the editor's suite, because it reads types.ts as
// TEXT and only vitest runs next to it; editor-v2/utils/conditionFields.ts
// re-exports this set so the test keeps seeing it under the name it always
// had, and so this stays the only copy on the condition path.
export const INPUT_BLOCK_TYPES = new Set([
  'text', 'textarea', 'email', 'phone', 'number', 'url',
  'radio', 'checkbox', 'select', 'boolean', 'rating', 'nps', 'consent', 'slider',
]);

// A form carries its texts under the language key the form itself was built
// in, which is NOT always 'de': `form_data.defaultLanguage` holds it, and the
// rest of this codebase falls back to 'de-DE', not 'de' (see
// src/components/modals/context/contextItemForm.vue and
// src/editor-v2/forms/logicEditor.helpers.ts#fieldLabel). Guessing the key
// wrong is not a small miss: every label silently falls through to the block
// id, and the picker shows `f_dk3oruro` where the question should be.
const FALLBACK_LANGUAGE = 'de-DE';

// The words this module writes itself. editor-v2 passes its own in its
// interface language; the Vue-2 admin passes none and keeps German.
const DEFAULT_LABELS = {
  answersGroup: 'Antworten · {name}',
  pageGroup: 'Auf dieser Seite · {name}',
  submitted: 'Formular abgeschickt',
};

function label(entry, language, fallback) {
  return (entry && entry.label && entry.label[language]) || fallback;
}

// A label more than one form carries ("Formular abgeschickt" always does,
// "Σ Gesamt" often) reads the same in a flat picker and in a sentence, and the
// editor cannot tell which form it points at. Those labels get the form name
// behind them; a label only one form has stays as it is. Paths never change.
// `shortLabel` keeps the bare label for places with no room for the form name
// (the canvas chip).
function disambiguate(groups) {
  const formsPerLabel = new Map();
  groups.forEach((group) => {
    new Set(group.fields.map((field) => field.label)).forEach((text) => {
      formsPerLabel.set(text, (formsPerLabel.get(text) || 0) + 1);
    });
  });
  return groups.map((group) => ({
    ...group,
    fields: group.fields.map((field) => (formsPerLabel.get(field.label) > 1
      ? { ...field, label: `${field.label} (${group.formName})`, shortLabel: field.label }
      : field)),
  }));
}

// The forms placed on the page being edited come first: they are what an
// editor on that page almost always means. Only a caller that knows the page
// passes ids (the editor-v2 canvas does); everyone else keeps the old order.
function pageFirst(groups, onPage) {
  const here = groups.filter((group) => onPage.has(String(group.formId)));
  const elsewhere = groups.filter((group) => !onPage.has(String(group.formId)));
  return [...here, ...elsewhere];
}

function formGroups(forms, labels, pageFormIds) {
  if (!Array.isArray(forms)) return [];

  const groups = [];
  forms.forEach((form) => {
    const doc = form.form_data;
    const language = (doc && doc.defaultLanguage) || FALLBACK_LANGUAGE;
    const fields = [];
    ((doc && doc.pages) || []).forEach((page) => {
      (page.blocks || []).forEach((block) => {
        if (!INPUT_BLOCK_TYPES.has(block.type)) return;
        const field = {
          path: `form.${form.form_id}.${block.id}`,
          label: label(block, language, block.id),
          kind: 'form',
          fieldType: block.type,
        };
        if (block.options) {
          field.options = block.options.map((option) => ({
            value: option.value,
            label: label(option, language, option.value),
          }));
        }
        fields.push(field);
      });
    });
    if (fields.length > 0) {
      // Calculated variables and "Formular abgeschickt" come after the
      // questions, and only for a form that has any: a form nobody can answer
      // is never submitted and has nothing to calculate from.
      derivedFormFields(doc, labels).forEach((derived) => {
        fields.push({
          path: `form.${form.form_id}.${derived.fieldId}`,
          label: derived.label,
          kind: 'form',
          fieldType: derived.fieldType,
          derived: derived.derived,
        });
      });
      groups.push({ formId: form.form_id, formName: form.name, fields });
    }
  });
  const named = disambiguate(groups);

  const onPage = new Set((Array.isArray(pageFormIds) ? pageFormIds : []).map((id) => String(id)));
  return pageFirst(named, onPage).map((group) => {
    const heading = onPage.has(String(group.formId)) ? labels.pageGroup : labels.answersGroup;
    // A replacer function, so "$&" in a form name stays literal.
    return { label: heading.replace('{name}', () => group.formName), fields: group.fields };
  });
}

function personalizationGroup(sets) {
  if (!Array.isArray(sets) || sets.length === 0) return [];

  const fields = [];
  sets.forEach((set) => {
    (set.schema || []).forEach((entry) => {
      fields.push({
        path: `${set.personalized_variable_set_id}.${entry.id}`,
        label: sets.length > 1 ? `${set.name}: ${entry.name}` : entry.name,
        kind: 'personalization',
        fieldType: 'text',
      });
    });
  });

  // The set itself, as a condition. The reader fills
  // `personalized_variable_set_id` in buildData; the value is the set id.
  fields.push({
    path: 'personalized_variable_set_id',
    label: 'Das Personalisierungs-Set selbst',
    kind: 'personalization',
    fieldType: 'text',
    options: sets.map((set) => ({
      value: String(set.personalized_variable_set_id),
      label: set.name,
    })),
  });

  return [{ label: 'Personalisierung', fields }];
}

function urlParameterGroup() {
  // Nobody can list the possible parameter names, so this group holds one
  // entry and the panel turns it into a name box.
  return {
    label: 'Adresszeile',
    fields: [{
      path: '', label: 'Parameter aus der Adresszeile', kind: 'urlParameter', fieldType: 'text',
    }],
  };
}

function segmentGroup(segments) {
  if (!Array.isArray(segments) || segments.length === 0) return [];
  return [{
    label: 'Segmente',
    fields: segments.map((segment) => ({
      path: segment.id, label: segment.name, kind: 'segment', fieldType: 'segment',
    })),
  }];
}

/**
 * Every field a condition can point at, grouped by source.
 *
 * @param {object} [sources]
 * @param {Array} [sources.forms]  The shape `getFormsByPath` returns:
 *                                 `[{ form_id, name, form_data }]`.
 * @param {Array} [sources.personalizationSets]
 *                                 `[{ personalized_variable_set_id, name,
 *                                    schema: [{ id, name }] }]`.
 * @param {Array} [sources.segments]  `[{ id, name }]`.
 * @param {boolean} [sources.allowUrlParameterAlone]  See the gate below.
 * @param {Array} [sources.pageFormIds]  Ids of the forms placed on the page
 *                                 being edited; their groups come first under
 *                                 `labels.pageGroup`. Left out, the order stays.
 * @param {object} [sources.labels]  `{ answersGroup, pageGroup, submitted }`;
 *                                 both group headings hold `{name}`.
 * @returns {Array} `[{ label, fields: [{ path, label, kind, fieldType,
 *                     options? }] }]`.
 */
export function collectConditionFields(sources) {
  const input = sources || {};
  const labels = { ...DEFAULT_LABELS, ...(input.labels || {}) };
  const answers = formGroups(input.forms, labels, input.pageFormIds);
  const personalization = personalizationGroup(input.personalizationSets);
  const segments = segmentGroup(input.segments);

  // The address bar is always available in principle, but offering it alone
  // would put a condition editor in front of a customer who has nothing to
  // build one from -- the same "a choice with nothing behind it" the menu
  // entry's own gate avoids. It only joins once something else is there.
  //
  // `allowUrlParameterAlone` lifts that gate, and it is the CALLER's choice
  // because the answer differs by caller, not by data:
  //   - an ELEMENT's condition needs something to condition ON, so an editor
  //     with no source at all really has nothing to offer -- the editor never
  //     sets this;
  //   - a SEGMENT is a named condition in its own right, and "Kampagne
  //     Sommer" (one address-bar parameter, nothing else) is a whole segment
  //     on its own. The old three-modal chain could build it in a project
  //     without a single form, and taking that away would be taking a working
  //     feature from customers because of a rule borrowed from the other
  //     caller.
  const nothingElse = answers.length === 0 && personalization.length === 0
    && segments.length === 0;
  if (nothingElse && !input.allowUrlParameterAlone) return [];

  return [...answers, ...personalization, urlParameterGroup(), ...segments];
}

/**
 * The field behind a stored row, or null when it is gone.
 *
 * @param {Array} groups  What `collectConditionFields` returned.
 * @param {string} path
 * @param {string} kind  'form' | 'personalization' | 'urlParameter' | 'segment'.
 * @returns {object|null}
 */
export function fieldForPath(groups, path, kind) {
  for (let i = 0; i < groups.length; i += 1) {
    const hit = groups[i].fields.find((f) => f.kind === kind && f.path === path);
    if (hit) return hit;
  }
  return null;
}
